/**
 * Exact SHAP explanations for the AA_models region classifiers — the values
 * behind the native `*_report_interpretation.pdf` force plots
 * (`src/ads/reporting/interpretation/shap_interpretation.py`,
 * `shap_plot_robust` / `shap_plot_aspectpc`, shap 0.50.0).
 *
 * TreeSHAP here is Lundberg's path-dependent algorithm, i.e.
 * `shap.TreeExplainer(rf)` with its default
 * `feature_perturbation="tree_path_dependent"` (port of shap's
 * `tree_shap_recursive` / `extend_path` / `unwind_path` /
 * `unwound_path_sum`). Forests are read from `public/models/aa-shap/`
 * (exported offline by `scripts/export-aa-shap-forests.py`; format documented
 * there). The ONNX copies under `public/models/aa/` cannot be used: TreeSHAP
 * needs every node's training cover, which ONNX drops.
 *
 * Native quirks reproduced on purpose (so numbers match the native PDFs):
 *  - The model input is the QFV CSV row (region "portion injured" + `volml`
 *    log10-mL), NOT the mL vectors `interpretation.ts` feeds the ONNX models;
 *    the first column is displayed as "logvol" for VASCULAR/LOBE (native
 *    `INTERPRETATION_FEATURES`), and values are `round(4)`ed (numpy
 *    half-even) before scaling.
 *  - The explained output is the argmax class of `predict_proba`
 *    (`_pick_output_index`), so "NORMAL" calls show P(class 0).
 *  - Explanations are on the StandardScaler-transformed input, cast to
 *    float32 (shap/sklearn `input_dtype`), shown with the unscaled values.
 *  - ASPECTSPC: the native pipeline renames the CSV's `Cerebellum/Pons/
 *    Midbrain` to lowercase, then reindexes to the model's capitalised
 *    `feature_cols` with `fill_value=0` — so those 3 inputs (and their
 *    `*_x_vol` interactions) are always 0. Two of its models (Thalamus,
 *    midbrain) are LogisticRegression explained with a one-row background:
 *    every SHAP value is exactly 0 and base = output = the decision function.
 */

import { fetchAsset } from "../assets";

export type InterpretationAtlas = "ASPECTS" | "ASPECTSPC" | "LOBE" | "VASCULAR";

interface ForestManifestEntry {
  roi: string;
  featureCols: string[];
  scalerMean: number[];
  scalerScale: number[];
  kind: "forest";
  file: string;
  nTrees: number;
  nNodes: number;
  valueEncoding: "u16" | "f64";
}

interface LinearManifestEntry {
  roi: string;
  featureCols: string[];
  scalerMean: number[];
  scalerScale: number[];
  kind: "linear";
  coef: number[];
  intercept: number;
}

type ManifestEntry = ForestManifestEntry | LinearManifestEntry;

interface AtlasManifest {
  atlas: string;
  /** Native `INTERPRETATION_FEATURES` order (the QFV CSV columns, renamed). */
  features: string[];
  models: ManifestEntry[];
}

/** A RandomForest in flat arrays; tree t owns nodes [offsets[t], offsets[t+1]). */
export interface Forest {
  nTrees: number;
  offsets: Int32Array;
  threshold: Float32Array;
  right: Int16Array;
  feature: Int8Array;
  cover: Float64Array;
  /** Leaf value for class 0 / class 1 (probability), per node. */
  value0: Float64Array;
  value1: Float64Array;
  maxDepth: number;
}

export interface ShapExplanation {
  /** ROI / model name — the force plot's `out_names` label. */
  roi: string;
  featureNames: string[];
  /** Display values (`X.round(4)`, unscaled) — the "= value" in the labels. */
  data: number[];
  /** SHAP value per feature for the explained class. */
  values: number[];
  baseValue: number;
  /** baseValue + sum(values). */
  outValue: number;
  /** Explained class (argmax of predict_proba; 1 for linear models). */
  outputIndex: 0 | 1;
  kind: "forest" | "linear";
}

/** Loads a file under `public/models/aa-shap/` (path relative to that folder). */
export type ShapAssetLoader = (relPath: string) => Promise<ArrayBuffer>;

const ATLAS_DIR: Record<InterpretationAtlas, string> = {
  VASCULAR: "vascular",
  LOBE: "lobe",
  ASPECTS: "aspects",
  ASPECTSPC: "aspectspc",
};

export const defaultShapLoader: ShapAssetLoader = async (relPath) => {
  const url = `/models/aa-shap/${relPath}`;
  const res = await fetchAsset(url);
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return res.arrayBuffer();
};

// ---------------------------------------------------------------------------
// numpy-compatible helpers
// ---------------------------------------------------------------------------

/** `np.rint`: round half to even. */
function rint(y: number): number {
  const r = Math.round(y);
  return Math.abs(y - Math.trunc(y)) === 0.5 ? 2 * Math.round(y / 2) : r;
}

/** `np.round(x, 4)` / `pd.Series.round(4)` (multiply, rint, divide). */
export function round4(x: number): number {
  return rint(x * 1e4) / 1e4;
}

// ---------------------------------------------------------------------------
// Forest loading
// ---------------------------------------------------------------------------

function parseForest(buf: ArrayBuffer, m: ForestManifestEntry): Forest {
  const T = m.nTrees;
  const N = m.nNodes;
  let o = 0;
  const offsets = new Int32Array(buf.slice(o, o + 4 * (T + 1)));
  o += 4 * (T + 1);
  o += (8 - (o % 8)) % 8;
  let value1: Float64Array;
  let value0: Float64Array;
  let cover: Float64Array;
  if (m.valueEncoding === "f64") {
    value1 = new Float64Array(buf.slice(o, o + 8 * N));
    o += 8 * N;
    cover = new Float64Array(buf.slice(o, o + 8 * N));
    o += 8 * N;
    value0 = new Float64Array(N);
    for (let i = 0; i < N; i++) value0[i] = 1 - value1[i];
  }
  const threshold = new Float32Array(buf.slice(o, o + 4 * N));
  o += 4 * N;
  if (m.valueEncoding === "u16") {
    const c1 = new Uint16Array(buf.slice(o, o + 2 * N));
    o += 2 * N;
    const cv = new Uint16Array(buf.slice(o, o + 2 * N));
    o += 2 * N;
    value1 = new Float64Array(N);
    value0 = new Float64Array(N);
    cover = new Float64Array(N);
    for (let i = 0; i < N; i++) {
      cover[i] = cv[i];
      // Same float64 divisions sklearn performs (count / weighted_n_node_samples).
      value1[i] = c1[i] / cv[i];
      value0[i] = (cv[i] - c1[i]) / cv[i];
    }
  }
  const right = new Int16Array(buf.slice(o, o + 2 * N));
  o += 2 * N;
  const feature = new Int8Array(buf.slice(o, o + N));
  o += N;
  if (o !== buf.byteLength) throw new Error(`forest ${m.file}: size mismatch (${o} != ${buf.byteLength})`);

  let maxDepth = 0;
  const depth = new Int32Array(1024);
  for (let t = 0; t < T; t++) {
    const base = offsets[t];
    const n = offsets[t + 1] - base;
    depth[0] = 0;
    for (let i = 0; i < n; i++) {
      const d = depth[i];
      if (right[base + i] === -1) {
        if (d > maxDepth) maxDepth = d;
      } else {
        depth[i + 1] = d + 1;
        depth[right[base + i]] = d + 1;
      }
    }
  }
  return { nTrees: T, offsets, threshold, right, feature, cover: cover!, value0: value0!, value1: value1!, maxDepth };
}

interface LoadedModel {
  entry: ManifestEntry;
  forest?: Forest;
}

const atlasCache = new Map<string, Promise<{ manifest: AtlasManifest; models: LoadedModel[] }>>();

async function loadAtlas(atlas: InterpretationAtlas, loader: ShapAssetLoader) {
  const key = atlas + (loader === defaultShapLoader ? "" : "#custom");
  let p = atlasCache.get(key);
  if (!p) {
    p = (async () => {
      const dir = ATLAS_DIR[atlas];
      const manifest = JSON.parse(new TextDecoder().decode(await loader(`${dir}/manifest.json`))) as AtlasManifest;
      const models = await Promise.all(
        manifest.models.map(async (entry): Promise<LoadedModel> => {
          if (entry.kind === "linear") return { entry };
          return { entry, forest: parseForest(await loader(`${dir}/${entry.file}`), entry) };
        }),
      );
      return { manifest, models };
    })();
    if (loader === defaultShapLoader) atlasCache.set(key, p);
    p.catch(() => atlasCache.delete(key));
  }
  return p;
}

// ---------------------------------------------------------------------------
// Prediction + TreeSHAP (path-dependent)
// ---------------------------------------------------------------------------

function leafIndex(f: Forest, t: number, x: Float32Array): number {
  const base = f.offsets[t];
  let n = 0;
  while (f.right[base + n] !== -1) {
    n = x[f.feature[base + n]] <= f.threshold[base + n] ? n + 1 : f.right[base + n];
  }
  return base + n;
}

/** sklearn `predict_proba(X)[0]` on an already-scaled float32 row. */
export function forestPredictProba(f: Forest, x: Float32Array): [number, number] {
  let s0 = 0;
  let s1 = 0;
  for (let t = 0; t < f.nTrees; t++) {
    const leaf = leafIndex(f, t, x);
    s0 += f.value0[leaf];
    s1 += f.value1[leaf];
  }
  return [s0 / f.nTrees, s1 / f.nTrees];
}

class PathBuf {
  feat: Int32Array;
  zero: Float64Array;
  one: Float64Array;
  pw: Float64Array;
  constructor(size: number) {
    this.feat = new Int32Array(size);
    this.zero = new Float64Array(size);
    this.one = new Float64Array(size);
    this.pw = new Float64Array(size);
  }
}

function extendPath(p: PathBuf, o: number, depth: number, zero: number, one: number, feat: number): void {
  p.feat[o + depth] = feat;
  p.zero[o + depth] = zero;
  p.one[o + depth] = one;
  p.pw[o + depth] = depth === 0 ? 1 : 0;
  for (let i = depth - 1; i >= 0; i--) {
    p.pw[o + i + 1] += (one * p.pw[o + i] * (i + 1)) / (depth + 1);
    p.pw[o + i] = (zero * p.pw[o + i] * (depth - i)) / (depth + 1);
  }
}

function unwindPath(p: PathBuf, o: number, depth: number, pathIndex: number): void {
  const one = p.one[o + pathIndex];
  const zero = p.zero[o + pathIndex];
  let nextOnePortion = p.pw[o + depth];
  for (let i = depth - 1; i >= 0; i--) {
    if (one !== 0) {
      const tmp = p.pw[o + i];
      p.pw[o + i] = (nextOnePortion * (depth + 1)) / ((i + 1) * one);
      nextOnePortion = tmp - (p.pw[o + i] * zero * (depth - i)) / (depth + 1);
    } else {
      p.pw[o + i] = (p.pw[o + i] * (depth + 1)) / (zero * (depth - i));
    }
  }
  for (let i = pathIndex; i < depth; i++) {
    p.feat[o + i] = p.feat[o + i + 1];
    p.zero[o + i] = p.zero[o + i + 1];
    p.one[o + i] = p.one[o + i + 1];
  }
}

function unwoundPathSum(p: PathBuf, o: number, depth: number, pathIndex: number): number {
  const one = p.one[o + pathIndex];
  const zero = p.zero[o + pathIndex];
  let nextOnePortion = p.pw[o + depth];
  let total = 0;
  for (let i = depth - 1; i >= 0; i--) {
    if (one !== 0) {
      const tmp = (nextOnePortion * (depth + 1)) / ((i + 1) * one);
      total += tmp;
      nextOnePortion = p.pw[o + i] - tmp * zero * ((depth - i) / (depth + 1));
    } else if (zero !== 0) {
      total += p.pw[o + i] / zero / ((depth - i) / (depth + 1));
    }
  }
  return total;
}

function treeShapRecursive(
  f: Forest, base: number, values: Float64Array, scale: number, x: Float32Array, phi: Float64Array,
  node: number, p: PathBuf, parentOff: number, depth: number,
  parentZero: number, parentOne: number, parentFeat: number,
): void {
  // Each recursion level gets its own copy of the path, right after the
  // parent's (shap: `unique_path = parent_unique_path + unique_depth + 1`).
  const o = parentOff + depth + 1;
  if (depth > 0) {
    p.feat.copyWithin(o, parentOff, parentOff + depth);
    p.zero.copyWithin(o, parentOff, parentOff + depth);
    p.one.copyWithin(o, parentOff, parentOff + depth);
    p.pw.copyWithin(o, parentOff, parentOff + depth);
  }
  extendPath(p, o, depth, parentZero, parentOne, parentFeat);
  const gi = base + node;
  const r = f.right[gi];
  if (r === -1) {
    const leafValue = values[gi] * scale;
    for (let i = 1; i <= depth; i++) {
      const w = unwoundPathSum(p, o, depth, i);
      phi[p.feat[o + i]] += w * (p.one[o + i] - p.zero[o + i]) * leafValue;
    }
    return;
  }
  const split = f.feature[gi];
  const left = node + 1;
  const hot = x[split] <= f.threshold[gi] ? left : r;
  const cold = hot === left ? r : left;
  const w = f.cover[gi];
  const hotZero = f.cover[base + hot] / w;
  const coldZero = f.cover[base + cold] / w;
  let incomingZero = 1;
  let incomingOne = 1;
  let pathIndex = 0;
  for (; pathIndex <= depth; pathIndex++) if (p.feat[o + pathIndex] === split) break;
  let d = depth;
  if (pathIndex !== depth + 1) {
    incomingZero = p.zero[o + pathIndex];
    incomingOne = p.one[o + pathIndex];
    unwindPath(p, o, d, pathIndex);
    d -= 1;
  }
  treeShapRecursive(f, base, values, scale, x, phi, hot, p, o, d + 1, hotZero * incomingZero, incomingOne, split);
  treeShapRecursive(f, base, values, scale, x, phi, cold, p, o, d + 1, coldZero * incomingZero, 0, split);
}

/**
 * Path-dependent TreeSHAP for one row (already scaled, float32) and one
 * class. Returns per-feature SHAP values and the base value
 * (`explainer.expected_value[cls]` = mean root value).
 */
export function forestTreeShap(f: Forest, x: Float32Array, cls: 0 | 1): { values: Float64Array; base: number } {
  const nFeat = x.length;
  const phi = new Float64Array(nFeat + 1); // slot nFeat absorbs the root's dummy feature (-1)
  const values = cls === 1 ? f.value1 : f.value0;
  const scale = 1 / f.nTrees;
  const D = f.maxDepth + 2;
  const p = new PathBuf(((D + 3) * (D + 4)) / 2);
  let base = 0;
  for (let t = 0; t < f.nTrees; t++) {
    const b = f.offsets[t];
    base += values[b] * scale;
    treeShapRecursive(f, b, values, scale, x, phi, 0, p, 0, 0, 1, 1, nFeat);
  }
  return { values: phi.subarray(0, nFeat), base };
}

// ---------------------------------------------------------------------------
// Native feature handling
// ---------------------------------------------------------------------------

/** QFV CSV header names per atlas (`*_QFV.csv`, after `subject_id`). */
export const QFV_COLUMNS: Record<InterpretationAtlas, string[]> = {
  VASCULAR: ["volml", "ACA", "MCA", "PCA", "cerebellar", "basilar", "Lenticulostriate", "Choroidal & Thalamoperfurating", "watershed"],
  LOBE: ["volml", "basal ganglia", "deep white matter", "cerebellum", "frontal", "insula", "internal capsule", "brainstem", "occipital", "parietal", "temporal", "thalamus"],
  ASPECTS: ["volml", "Caudate", "lentiform", "IC", "insula", "M1", "M2", "M3", "M6", "M5", "M4"],
  ASPECTSPC: ["volml", "PCA", "Thalamus", "Cerebellum", "Pons", "Midbrain"],
};

/**
 * Native `ReportPipeline._row_vector` + `.reindex(feat_names).round(4)`:
 * QFV columns are renamed positionally to the native feature names, missing
 * values -> 0. Lookup accepts either the CSV header or the native name
 * (exact, then case-insensitive).
 */
function nativeSeries(atlas: InterpretationAtlas, nativeNames: string[], features: Map<string, number>): number[] {
  const lower = new Map<string, number>();
  for (const [k, v] of features) lower.set(k.toLowerCase(), v);
  return nativeNames.map((name, i) => {
    const csvName = QFV_COLUMNS[atlas][i];
    const v =
      features.get(csvName) ?? features.get(name) ??
      lower.get(csvName.toLowerCase()) ?? lower.get(name.toLowerCase()) ??
      (i === 0 ? features.get("logvol") : undefined);
    return round4(v !== undefined && Number.isFinite(v) ? v : 0);
  });
}

/** `_add_aspectpc_engineered_features` + reindex to `feature_cols` (fill 0). */
function aspectPcModelRow(names: string[], series: number[], featureCols: string[]): number[] {
  const col = new Map<string, number>();
  names.forEach((n, i) => col.set(n, series[i]));
  const c = (n: string) => col.get(n) ?? 0;
  const vol = c("volml");
  const logvol = Math.log10(Math.max(vol, 1e-6));
  col.set("logvol", logvol);
  col.set("sqrtvol", Math.sqrt(Math.max(vol, 0)));
  // `_col(name)` is case-sensitive: "Cerebellum"/"Pons"/"Midbrain" are absent
  // (the series carries lowercase names) -> 0, exactly as native.
  col.set("PCA_x_vol", c("PCA") * logvol);
  col.set("Thalamus_x_vol", c("Thalamus") * logvol);
  col.set("Cerebellum_x_vol", c("Cerebellum") * logvol);
  col.set("Pons_x_vol", c("Pons") * logvol);
  col.set("Midbrain_x_vol", c("Midbrain") * logvol);
  return featureCols.map((n) => col.get(n) ?? 0);
}

function scaleRow(row: number[], m: ManifestEntry): Float64Array {
  const out = new Float64Array(row.length);
  for (let i = 0; i < row.length; i++) out[i] = (row[i] - m.scalerMean[i]) / m.scalerScale[i];
  return out;
}

/**
 * SHAP explanations for every model of one atlas, in native page order.
 * `features`: one QFV row keyed by the QFV CSV header (see `QFV_COLUMNS`;
 * `qfvCsvToFeatureMap` in `shapPdf.ts` builds it from the CSV text).
 */
export async function explainAtlas(
  atlas: InterpretationAtlas,
  features: Map<string, number>,
  loader: ShapAssetLoader = defaultShapLoader,
): Promise<ShapExplanation[]> {
  const { manifest, models } = await loadAtlas(atlas, loader);
  const series = nativeSeries(atlas, manifest.features, features);
  const out: ShapExplanation[] = [];
  for (const { entry, forest } of models) {
    let rawRow: number[];
    let names: string[];
    if (atlas === "ASPECTSPC") {
      rawRow = aspectPcModelRow(manifest.features, series, entry.featureCols);
      names = entry.featureCols;
    } else {
      rawRow = series;
      names = manifest.features;
    }
    const disp = rawRow.map(round4);
    const xDisp = scaleRow(disp, entry);
    if (entry.kind === "linear") {
      let z = entry.intercept;
      for (let i = 0; i < xDisp.length; i++) z += entry.coef[i] * xDisp[i];
      out.push({
        roi: entry.roi, featureNames: names, data: disp, values: disp.map(() => 0),
        baseValue: z, outValue: z, outputIndex: 1, kind: "linear",
      });
      continue;
    }
    const f = forest!;
    // `_pick_output_index`: argmax of predict_proba on the (unrounded) X.
    const [p0, p1] = forestPredictProba(f, Float32Array.from(scaleRow(rawRow, entry)));
    const cls: 0 | 1 = p1 > p0 ? 1 : 0;
    const { values, base } = forestTreeShap(f, Float32Array.from(xDisp), cls);
    let sum = base;
    for (const v of values) sum += v;
    out.push({
      roi: entry.roi, featureNames: names, data: disp, values: Array.from(values),
      baseValue: base, outValue: sum, outputIndex: cls, kind: "forest",
    });
  }
  return out;
}
